import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  TextInput,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Animated,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  Pressable,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Search, X, MapPin, Check } from 'lucide-react-native';

import { useNominatimSearch, OtherCityData } from '@/api';
import { COLORS } from '@/constants';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const MODAL_HEIGHT = SCREEN_HEIGHT * 0.7;

type CitySearchModalProps = {
  visible: boolean;
  onClose: () => void;
  onSelect: (city: OtherCityData) => void;
  initialValue?: OtherCityData | null;
};

const CitySearchModal: React.FC<CitySearchModalProps> = ({
  visible,
  onClose,
  onSelect,
  initialValue,
}) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<OtherCityData | null>(initialValue || null);
  
  const slideAnim = useRef(new Animated.Value(MODAL_HEIGHT)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Animation on open/close
  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: MODAL_HEIGHT,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, slideAnim, backdropAnim]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setSearchQuery('');
      setDebouncedQuery('');
      setSelectedCity(initialValue || null);
    }
  }, [visible, initialValue]);

  const { data: searchResults = [], isLoading, isFetching } = useNominatimSearch(debouncedQuery);

  const handleSelectCity = useCallback((city: OtherCityData) => {
    setSelectedCity(city);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedCity) {
      onSelect(selectedCity);
      onClose();
    }
  }, [selectedCity, onSelect, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Colors
  const backgroundColor = isDark ? '#1a1a1a' : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#18181b';
  const subtitleColor = isDark ? '#a1a1aa' : '#71717a';
  const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const inputBg = isDark ? '#27272a' : '#f4f4f5';
  const itemBg = isDark ? '#27272a' : '#f9fafb';
  const selectedBg = isDark ? 'rgba(94, 128, 156, 0.3)' : 'rgba(94, 128, 156, 0.15)';

  const formatCityDisplay = (city: OtherCityData) => {
    const parts: string[] = [];
    if (city.city) parts.push(city.city);
    if (city.region) parts.push(city.region);
    if (city.state) parts.push(city.state);
    if (city.country) parts.push(city.country);
    return parts.join(', ') || city.displayName;
  };

  const renderCityItem = useCallback(
    ({ item }: { item: OtherCityData }) => {
      const isSelected = selectedCity?.lat === item.lat && selectedCity?.lon === item.lon;
      
      return (
        <TouchableOpacity
          onPress={() => handleSelectCity(item)}
          style={[
            styles.cityItem,
            {
              backgroundColor: isSelected ? selectedBg : itemBg,
              borderColor: isSelected ? COLORS.PRIMARY : borderColor,
              borderWidth: isSelected ? 2 : 1,
            },
          ]}
        >
          <XStack flex={1} alignItems="flex-start" space="$2">
            <MapPin size={18} color={isSelected ? COLORS.PRIMARY : subtitleColor} style={{ marginTop: 2 }} />
            <YStack flex={1}>
              <Text fontSize="$4" fontWeight="500" color={textColor} numberOfLines={1}>
                {item.city || item.displayName.split(',')[0]}
              </Text>
              <Text fontSize="$3" color={subtitleColor} numberOfLines={2}>
                {formatCityDisplay(item)}
              </Text>
            </YStack>
          </XStack>
          {isSelected && (
            <Check size={20} color={COLORS.PRIMARY} />
          )}
        </TouchableOpacity>
      );
    },
    [selectedCity, handleSelectCity, selectedBg, itemBg, borderColor, textColor, subtitleColor]
  );

  const renderEmptyState = () => {
    if (isLoading || isFetching) {
      return (
        <YStack flex={1} alignItems="center" justifyContent="center" paddingVertical="$6">
          <ActivityIndicator size="large" color={COLORS.PRIMARY} />
          <Text color={subtitleColor} marginTop="$3">
            {t('components.citySearch.searching', 'Szukam...')}
          </Text>
        </YStack>
      );
    }

    if (debouncedQuery.length >= 2 && searchResults.length === 0) {
      return (
        <YStack flex={1} alignItems="center" justifyContent="center" paddingVertical="$6">
          <MapPin size={48} color={subtitleColor} />
          <Text color={subtitleColor} marginTop="$3" textAlign="center">
            {t('components.citySearch.noResults', 'Nie znaleziono wyników')}
          </Text>
        </YStack>
      );
    }

    return (
      <YStack flex={1} alignItems="center" justifyContent="center" paddingVertical="$6">
        <Search size={48} color={subtitleColor} />
        <Text color={subtitleColor} marginTop="$3" textAlign="center">
          {t('components.citySearch.hint', 'Wpisz nazwę miasta aby wyszukać')}
        </Text>
      </YStack>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        {/* Backdrop */}
        <Animated.View
          style={[
            styles.backdrop,
            {
              opacity: backdropAnim,
            },
          ]}
        >
          <Pressable style={styles.backdropPressable} onPress={handleClose} />
        </Animated.View>

        {/* Bottom Sheet */}
        <Animated.View
          style={[
            styles.bottomSheet,
            {
              backgroundColor,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Handle */}
          <YStack alignItems="center" paddingVertical="$2">
            <YStack
              width={40}
              height={4}
              borderRadius={2}
              backgroundColor={borderColor}
            />
          </YStack>

          {/* Header */}
          <XStack
            paddingHorizontal="$4"
            paddingVertical="$2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="$5" fontWeight="600" color={textColor}>
              {t('components.citySearch.title', 'Wybierz miasto')}
            </Text>
            <TouchableOpacity onPress={handleClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <X size={24} color={subtitleColor} />
            </TouchableOpacity>
          </XStack>

          {/* Search Input */}
          <YStack paddingHorizontal="$4" paddingVertical="$2">
            <XStack
              backgroundColor={inputBg}
              borderRadius="$3"
              paddingHorizontal="$3"
              paddingVertical="$2"
              alignItems="center"
              space="$2"
            >
              <Search size={20} color={subtitleColor} />
              <TextInput
                style={getInputStyle(textColor)}
                placeholder={t('components.citySearch.placeholder', 'Wpisz nazwę miasta...')}
                placeholderTextColor={subtitleColor}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoFocus
                autoCorrect={false}
                autoCapitalize="words"
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <X size={18} color={subtitleColor} />
                </TouchableOpacity>
              )}
            </XStack>
          </YStack>

          {/* Results List */}
          <FlatList
            data={searchResults}
            renderItem={renderCityItem}
            keyExtractor={(item) => `${item.lat}-${item.lon}`}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={renderEmptyState}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          />

          {/* Confirm Button */}
          <YStack
            paddingHorizontal="$4"
            paddingVertical="$4"
            borderTopWidth={1}
            borderTopColor={borderColor}
          >
            <TouchableOpacity
              onPress={handleConfirm}
              disabled={!selectedCity}
              style={[
                styles.confirmButton,
                {
                  backgroundColor: selectedCity ? COLORS.PRIMARY : COLORS.DISABLED,
                },
              ]}
            >
              <Text color="white" fontSize="$4" fontWeight="600">
                {t('common.confirm', 'Zatwierdź')}
              </Text>
            </TouchableOpacity>
          </YStack>
        </Animated.View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles: { [key: string]: ViewStyle } = {
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...({ position: 'absolute' } as ViewStyle),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropPressable: {
    flex: 1,
  },
  bottomSheet: {
    height: MODAL_HEIGHT,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 20,
  },
  cityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginHorizontal: 16,
    marginVertical: 4,
    borderRadius: 12,
  },
  listContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  confirmButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
  },
};

const getInputStyle = (textColor: string): TextStyle => ({
  flex: 1,
  fontSize: 16,
  color: textColor,
  paddingVertical: 8,
});

export default CitySearchModal;

